import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTenant } from './useTenant'
import {
  listEmployees,
  getEmployee,
  listDepartments,
  getDepartment,
  listRoles,
  getRole,
  listPayrollCosts,
  getEmployeeStats,
  getOrganizationalHierarchy,
  getEmployeesByDepartment,
  getDirectReports,
  getManagementTree
} from '@/api/workforce'
import {
  Employee,
  Department,
  DepartmentWithDetails,
  Role,
  RoleWithDetails,
  PayrollCost,
  EmployeeFilters,
  PaginationParams,
  PaginatedResponse
} from '@/types/workforce'
import { queryKeys } from '@/constants/queryKeys'

/**
 * useEmployees - React Query hook for employee list
 *
 * Features:
 * - Automatic caching with 5-minute cache time
 * - Background refetching when data becomes stale (after 1 minute)
 * - Automatic refetch on window focus and network reconnect
 * - Tenant-isolated cache (no data leakage between tenants)
 *
 * @param filters - Optional filters (status, department, role, search, etc.)
 * @param pagination - Optional pagination params (limit, nextToken, sortBy, sortOrder)
 * @returns Employee list with loading state and refetch function
 */
export function useEmployees(filters?: EmployeeFilters, pagination?: PaginationParams) {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.workforce.employees.list(tenantDomain, filters, pagination),
    queryFn: async () => {
      const response = await listEmployees(tenantDomain, {
        ...filters,
        ...pagination
      })
      console.log('>>> [HOOK] useEmployees response:', response)
      const result = {
        items: response.items || [],
        count: response.pagination?.count || 0,
        nextToken: response.pagination?.nextToken
      }
      console.log('>>> [HOOK] useEmployees returning:', result)
      return result
    },
    enabled: !!tenantDomain, // Don't fetch until tenant is loaded
  })

  return {
    employees: data?.items || [],
    count: data?.count || 0,
    nextToken: data?.nextToken,
    loading: isLoading,
    isFetching, // Indicates background refetch in progress
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * useEmployee - React Query hook for single employee
 *
 * Features:
 * - Automatic caching with 5-minute cache time
 * - Background refetching when data becomes stale
 * - Tenant-isolated cache
 *
 * @param employeeId - Employee ID to fetch
 * @returns Single employee with loading state and refetch function
 */
export function useEmployee(employeeId: string | undefined) {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.workforce.employees.detail(tenantDomain, employeeId || ''),
    queryFn: async () => {
      if (!employeeId) throw new Error('Employee ID required')
      return await getEmployee(tenantDomain, employeeId)
    },
    enabled: !!tenantDomain && !!employeeId,
  })

  return {
    employee: data || null,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * useDepartments - React Query hook for department list
 *
 * Features:
 * - Automatic caching with 5-minute cache time
 * - Background refetching when data becomes stale (after 1 minute)
 * - Tenant-isolated cache
 *
 * @param pagination - Optional pagination params
 * @returns Department list with loading state and refetch function
 */
export function useDepartments(pagination?: PaginationParams) {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.workforce.departments.list(tenantDomain, pagination),
    queryFn: async () => {
      const departments = await listDepartments(tenantDomain)
      return {
        items: departments,
        count: departments.length
      }
    },
    enabled: !!tenantDomain,
  })

  return {
    departments: (data?.items || []) as DepartmentWithDetails[],
    count: data?.count || 0,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * useDepartment - React Query hook for single department
 *
 * Features:
 * - Automatic caching with 5-minute cache time
 * - Background refetching when data becomes stale
 * - Tenant-isolated cache
 *
 * @param departmentId - Department ID to fetch (UUID string)
 * @returns Single department with loading state and refetch function
 */
export function useDepartment(departmentId: string | undefined) {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.workforce.departments.detail(tenantDomain, departmentId || ''),
    queryFn: async () => {
      if (!departmentId) throw new Error('Department ID required')
      return await getDepartment(tenantDomain, departmentId)
    },
    enabled: !!tenantDomain && !!departmentId,
  })

  return {
    department: data as DepartmentWithDetails | null,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * useRoles - React Query hook for role list
 *
 * Features:
 * - Automatic caching with 5-minute cache time
 * - Background refetching when data becomes stale (after 1 minute)
 * - Tenant-isolated cache
 *
 * @param pagination - Optional pagination params
 * @returns Role list with loading state and refetch function
 */
export function useRoles(pagination?: PaginationParams) {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.workforce.roles.list(tenantDomain, pagination),
    queryFn: async () => {
      const response = await listRoles(tenantDomain)
      return {
        items: response.roles || [],
        count: response.pagination?.count || response.roles?.length || 0
      }
    },
    enabled: !!tenantDomain,
  })

  return {
    roles: (data?.items || []) as RoleWithDetails[],
    count: data?.count || 0,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * useRole - React Query hook for single role
 *
 * Features:
 * - Automatic caching with 5-minute cache time
 * - Background refetching when data becomes stale
 * - Tenant-isolated cache
 *
 * @param roleId - Role ID to fetch (UUID string)
 * @returns Single role with loading state and refetch function
 */
export function useRole(roleId: string | undefined) {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.workforce.roles.detail(tenantDomain, roleId || ''),
    queryFn: async () => {
      if (!roleId) throw new Error('Role ID required')
      return await getRole(tenantDomain, roleId)
    },
    enabled: !!tenantDomain && !!roleId,
  })

  return {
    role: data as RoleWithDetails | null,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * usePayroll - React Query hook for payroll costs list
 *
 * Features:
 * - Automatic caching with 5-minute cache time
 * - Background refetching when data becomes stale (after 1 minute)
 * - Tenant-isolated cache
 * - Supports filtering by department, date range
 *
 * @param filters - Optional filters (departmentId, startDate, endDate, period, employeeId, type, status)
 * @param pagination - Optional pagination params
 * @returns Payroll costs list with loading state and refetch function
 */
export function usePayroll(
  filters?: { period?: string; employeeId?: string; type?: string; status?: string; departmentId?: number; startDate?: string; endDate?: string },
  pagination?: PaginationParams
) {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.workforce.payroll.list(tenantDomain, filters, pagination),
    queryFn: async () => {
      const response = await listPayrollCosts(tenantDomain, {
        ...filters,
        ...pagination
      })
      return {
        items: response.items || [],
        count: response.pagination?.count || 0,
        nextToken: response.pagination?.nextToken
      }
    },
    enabled: !!tenantDomain,
  })

  return {
    payrollCosts: data?.items || [],
    count: data?.count || 0,
    nextToken: data?.nextToken,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * useEmployeeStats - React Query hook for employee statistics
 *
 * Features:
 * - Automatic caching with 5-minute cache time
 * - Background refetching for dashboard metrics
 * - Tenant-isolated cache
 *
 * @returns Employee statistics with loading state and refetch function
 */
export function useEmployeeStats() {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.workforce.employees.stats(tenantDomain),
    queryFn: async () => {
      return await getEmployeeStats(tenantDomain)
    },
    enabled: !!tenantDomain,
  })

  return {
    stats: data || null,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * useOrganizationalHierarchy - React Query hook for org hierarchy tree
 *
 * Features:
 * - Automatic caching with 5-minute cache time
 * - Background refetching for org chart data
 * - Tenant-isolated cache
 *
 * @returns Organizational hierarchy with loading state and refetch function
 */
export function useOrganizationalHierarchy() {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.workforce.hierarchy.tree(tenantDomain),
    queryFn: async () => {
      return await getOrganizationalHierarchy(tenantDomain)
    },
    enabled: !!tenantDomain,
  })

  return {
    hierarchy: data || null,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * useWorkforceDashboard - React Query hook for complete dashboard data
 *
 * Features:
 * - Fetches all workforce data in parallel (employees, departments, roles, stats, hierarchy)
 * - Automatic caching with 5-minute cache time
 * - Background refetching for dashboard overview
 * - Tenant-isolated cache
 * - Leverages individual query caches for efficiency
 *
 * @returns Complete dashboard data with loading state and refetch function
 */
export function useWorkforceDashboard() {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.workforce.dashboard.overview(tenantDomain),
    queryFn: async () => {
      // Fetch all required data in parallel
      const [employeesResponse, departmentsResponse, rolesResponse, statsResponse, hierarchyResponse] = await Promise.all([
        listEmployees(tenantDomain, { limit: 100 }),
        listDepartments(tenantDomain),
        listRoles(tenantDomain),
        getEmployeeStats(tenantDomain),
        getOrganizationalHierarchy(tenantDomain)
      ])

      return {
        employees: employeesResponse.items || [],
        departments: Array.isArray(departmentsResponse) ? departmentsResponse : [],
        roles: Array.isArray(rolesResponse) ? rolesResponse : [],
        stats: statsResponse,
        hierarchy: hierarchyResponse
      }
    },
    enabled: !!tenantDomain,
  })

  return {
    dashboardData: data || null,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * useEmployeesByDepartment - React Query hook for employees by department
 *
 * @param departmentId - Department ID to filter by
 * @param pagination - Optional pagination params
 * @returns Employees in department with loading state and refetch function
 */
export function useEmployeesByDepartment(departmentId: string | undefined, pagination?: PaginationParams) {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['workforce', 'departments', tenantDomain, departmentId, 'employees', pagination],
    queryFn: async () => {
      if (!departmentId) throw new Error('Department ID required')
      return await getEmployeesByDepartment(tenantDomain, parseInt(departmentId), pagination)
    },
    enabled: !!tenantDomain && !!departmentId,
  })

  return {
    employees: data?.items || [],
    count: data?.pagination.count || 0,
    nextToken: data?.pagination.nextToken,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * useDirectReports - React Query hook for employee's direct reports
 *
 * @param employeeId - Employee ID to get reports for
 * @param pagination - Optional pagination params
 * @returns Direct reports with loading state and refetch function
 */
export function useDirectReports(employeeId: string | undefined, pagination?: PaginationParams) {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['workforce', 'employees', tenantDomain, employeeId, 'reports', pagination],
    queryFn: async () => {
      if (!employeeId) throw new Error('Employee ID required')
      return await getDirectReports(tenantDomain, employeeId, pagination)
    },
    enabled: !!tenantDomain && !!employeeId,
  })

  return {
    reports: data?.items || [],
    count: data?.pagination.count || 0,
    nextToken: data?.pagination.nextToken,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}

/**
 * useManagementTree - React Query hook for employee's management tree
 *
 * @param employeeId - Employee ID to get management tree for
 * @param options - Optional maxDepth and includeManager flags
 * @returns Management tree with loading state and refetch function
 */
export function useManagementTree(
  employeeId: string | undefined,
  options?: { maxDepth?: number; includeManager?: boolean }
) {
  const { tenantDomain } = useTenant()

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['workforce', 'employees', tenantDomain, employeeId, 'management-tree', options],
    queryFn: async () => {
      if (!employeeId) throw new Error('Employee ID required')
      return await getManagementTree(tenantDomain, employeeId, options)
    },
    enabled: !!tenantDomain && !!employeeId,
  })

  return {
    tree: data || null,
    loading: isLoading,
    isFetching,
    error: error instanceof Error ? error.message : null,
    refetch
  }
}